import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {cn} from '@/lib/utils'

// Mirrors SIZE_BANDS/RISK_GROUPS in backend/app/core/partner.py; pricing bands, distinct from
// the 0312 chapters.
const SIZE_BANDS = ['1-10', '11-50', '51-100', '101-200', '201+'] as const
const RISK_GROUPS = ['I-III', 'IV-V'] as const

function segmentKey(size: string, risk: string) {
  return `${size}|${risk}`
}

function QuotaGrid({
  title,
  testId,
  renderCell,
}: {
  /** `null` cuando el contenedor ya imprime este mismo título. */
  title: string | null
  testId?: string
  renderCell: (size: (typeof SIZE_BANDS)[number], risk: (typeof RISK_GROUPS)[number], key: string) => ReactNode
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {title && <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">{title}</p>}
      <div className="surface rounded-lg p-3">
        <div className="grid grid-cols-[1fr_repeat(2,5.5rem)] items-center gap-x-3 gap-y-2">
          <span />
          {RISK_GROUPS.map((risk) => (
            <span
              key={risk}
              className="text-center font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {risk}
            </span>
          ))}
          {SIZE_BANDS.map((size) => (
            <div key={size} className="contents">
              <span className="font-mono text-xs text-foreground">{size}</span>
              {RISK_GROUPS.map((risk) => renderCell(size, risk, segmentKey(size, risk)))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function QuotaMatrixInput({
  value,
  onChange,
  idPrefix,
  title,
}: {
  value: Record<string, number>
  onChange: (next: Record<string, number>) => void
  idPrefix: string
  /** `null` apaga la versalita interna: la usa quien ya la imprime afuera. */
  title?: string | null
}) {
  const {t} = useTranslation()

  function setCell(key: string, raw: string) {
    const n = Math.max(0, parseInt(raw || '0', 10) || 0)
    onChange({...value, [key]: n})
  }

  return (
    <QuotaGrid
      title={title === undefined ? t('partners.quotaMatrix.title') : title}
      renderCell={(_size, _risk, key) => (
        <Input
          key={key}
          data-testid={`${idPrefix}-${key}`}
          type="number"
          min={0}
          className="h-8 text-center font-mono text-sm tabular-nums"
          value={String(value[key] ?? 0)}
          onChange={(e) => setCell(key, e.target.value)}
        />
      )}
    />
  )
}

function cellTone(quota: number, used: number): string {
  if (quota === 0) return 'text-muted-foreground'
  if (used >= quota) return 'text-tone-red-fg font-semibold'
  if (used >= quota - 1) return 'text-tone-amber-fg font-semibold'
  return 'text-foreground'
}

export type QuotaMatrixCell = {size_band: string; risk_group: string; quota: number; used: number}

export function QuotaMatrixDisplay({celdas}: {celdas: QuotaMatrixCell[]}) {
  const {t} = useTranslation()
  const bySegment = new Map(celdas.map((c) => [segmentKey(c.size_band, c.risk_group), c]))

  return (
    <QuotaGrid
      title={t('partnerPanel.quota.matrixTitle')}
      testId="partner-quota-matrix"
      renderCell={(_size, _risk, key) => {
        const cell = bySegment.get(key)
        const quota = cell?.quota ?? 0
        const used = cell?.used ?? 0
        return (
          <span
            key={key}
            data-testid={`partner-quota-cell-${key}`}
            className={cn('text-center font-mono text-xs tabular-nums', cellTone(quota, used))}>
            {used}/{quota}
          </span>
        )
      }}
    />
  )
}
